import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Mail, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog shown after the admin clicks 'Invia credenziali'.
 *
 * Always shows the manual link the admin can copy. If email was actually sent
 * (SendGrid configured), shows a green banner; otherwise an amber one with
 * instructions to share manually (WhatsApp, paper, etc.).
 */
export const CredentialsLinkDialog = ({ result, employee, onClose }) => {
    const [copied, setCopied] = useState(false);
    const open = !!result;
    const link = result?.manual_link || "";
    const sent = !!result?.sent;
    const email = result?.email || employee?.email || "";

    const handleCopy = async () => {
        let ok = false;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
                ok = true;
            }
        } catch (e) { /* try fallback */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = link;
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                ok = true;
            } catch (e) { /* ignore */ }
        }
        if (ok) {
            setCopied(true);
            toast.success("Link copiato negli appunti");
            setTimeout(() => setCopied(false), 2000);
        } else {
            toast.error("Impossibile copiare — selezionalo manualmente");
        }
    };

    const handleClose = () => {
        setCopied(false);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-lg" data-testid="creds-link-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Mail className="h-5 w-5 text-[#376DB3]" />
                        Credenziali generate
                    </DialogTitle>
                    <DialogDescription>
                        Link valido 1 ora per impostare la password di accesso al portale.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {sent ? (
                        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="creds-sent-banner">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <div>
                                <div className="font-medium">Email inviata a {email}</div>
                                <div className="text-xs">Il dipendente riceverà a breve il link di accesso.</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="creds-manual-banner">
                            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                            <div>
                                <div className="font-medium">Email automatica non attiva</div>
                                <div className="text-xs">
                                    Copia il link qui sotto e invialo manualmente al dipendente
                                    (WhatsApp, SMS, stampa, …). Quando configureremo SendGrid l'invio sarà automatico.
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="mb-1 text-xs font-medium text-slate-700">
                            Username: <span className="font-mono text-[#376DB3]">{email}</span>
                        </div>
                        <div className="mb-1 text-xs font-medium text-slate-700">Link reset password:</div>
                        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                            <input
                                readOnly
                                value={link}
                                className="flex-1 bg-transparent text-xs font-mono outline-none"
                                onFocus={(e) => e.target.select()}
                                data-testid="creds-link-input"
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCopy}
                                className="gap-1"
                                data-testid="creds-copy-btn"
                            >
                                {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                {copied ? "Copiato" : "Copia"}
                            </Button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={handleClose} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="creds-close-btn">
                        Chiudi
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
